import React, { useState, useEffect, useCallback } from 'react';
import {
  FaCube, FaPlus, FaTrash, FaCheck, FaChevronDown,
  FaBuilding, FaLink, FaLayerGroup, FaExternalLinkAlt,
  FaShieldAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../../config/api';
import './ToursManager.css';

// Matches the section_key values used in contractor_company_sections / fm_company_sections
const AREA_OPTIONS = [
  { key: 'roof',       label: 'Roof Safety' },
  { key: 'fire',       label: 'Fire Safety' },
  { key: 'electrical', label: 'Electrical Safety' },
  { key: 'staff',      label: 'Staff Safety' },
  { key: 'equipment',  label: 'Equipment Safety' },
  { key: 'building',   label: 'Building Maintenance' },
  { key: 'emergency',  label: 'Emergency Preparedness' },
];

export default function ToursManager() {
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');
  const [floors, setFloors] = useState([]);
  const [loadingProps, setLoadingProps] = useState(true);
  const [loadingFloors, setLoadingFloors] = useState(false);

  // New floor form
  const [newFloorName, setNewFloorName] = useState('');
  const [newFloorNumber, setNewFloorNumber] = useState('');
  const [newFloorAreas, setNewFloorAreas] = useState([]);
  const [addingFloor, setAddingFloor] = useState(false);

  // Expanded floor state
  const [expandedFloor, setExpandedFloor] = useState(null);

  // New link form (per floor) — URL only
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [addingLink, setAddingLink] = useState(false);

  // Per-floor area editing state: { [floorId]: string[] }
  const [floorAreaEdits, setFloorAreaEdits] = useState({});
  const [savingFloorAreas, setSavingFloorAreas] = useState({});

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  /* ── Load properties ── */
  useEffect(() => {
    fetch(getApiEndpoint('/api/cloud-staff/all-properties'), { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setProperties(Array.isArray(data) ? data : (data.properties || [])))
      .catch(() => setProperties([]))
      .finally(() => setLoadingProps(false));
  }, []);

  /* ── Load floors for selected property ── */
  const loadFloors = useCallback(() => {
    if (!selectedPropertyId) return;
    setLoadingFloors(true);
    fetch(getApiEndpoint(`/api/properties/${selectedPropertyId}/tours`), { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => setFloors(Array.isArray(data) ? data : []))
      .catch(() => setFloors([]))
      .finally(() => setLoadingFloors(false));
  }, [selectedPropertyId]);

  useEffect(() => {
    setFloors([]);
    setExpandedFloor(null);
    loadFloors();
  }, [selectedPropertyId, loadFloors]);

  /* ── Add floor ── */
  const handleAddFloor = async (e) => {
    e.preventDefault();
    if (!newFloorName.trim() || !selectedPropertyId) return;
    setAddingFloor(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${selectedPropertyId}/tours/floors`), {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          floor_name: newFloorName.trim(),
          floor_number: newFloorNumber !== '' ? parseInt(newFloorNumber, 10) : null,
          areas: newFloorAreas
        })
      });
      if (!res.ok) throw new Error();
      toast.success('Floor added');
      setNewFloorName('');
      setNewFloorNumber('');
      setNewFloorAreas([]);
      loadFloors();
    } catch {
      toast.error('Failed to add floor');
    } finally {
      setAddingFloor(false);
    }
  };

  /* ── Delete floor ── */
  const handleDeleteFloor = async (floorId) => {
    if (!window.confirm('Delete this floor and all its tours?')) return;
    try {
      const res = await fetch(getApiEndpoint(`/api/properties/${selectedPropertyId}/tours/floors/${floorId}`), {
        method: 'DELETE', headers
      });
      if (!res.ok) throw new Error();
      toast.success('Floor deleted');
      loadFloors();
    } catch {
      toast.error('Failed to delete floor');
    }
  };

  /* ── Add tour link ── */
  const handleAddLink = async (e, floorId) => {
    e.preventDefault();
    if (!newLinkUrl.trim()) return;
    setAddingLink(true);
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${selectedPropertyId}/tours/floors/${floorId}/links`),
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: newLinkUrl.trim(),
            url: newLinkUrl.trim()
          })
        }
      );
      if (!res.ok) throw new Error();
      toast.success('Tour link added');
      setNewLinkUrl('');
      loadFloors();
    } catch {
      toast.error('Failed to add tour link');
    } finally {
      setAddingLink(false);
    }
  };

  /* ── Delete link ── */
  const handleDeleteLink = async (floorId, linkId) => {
    if (!window.confirm('Remove this tour link?')) return;
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${selectedPropertyId}/tours/floors/${floorId}/links/${linkId}`),
        { method: 'DELETE', headers }
      );
      if (!res.ok) throw new Error();
      toast.success('Tour link removed');
      loadFloors();
    } catch {
      toast.error('Failed to remove link');
    }
  };

  /* ── Save floor area restrictions ── */
  const handleSaveFloorAreas = async (floorId) => {
    const areas = floorAreaEdits[floorId] ?? [];
    setSavingFloorAreas(prev => ({ ...prev, [floorId]: true }));
    try {
      const res = await fetch(
        getApiEndpoint(`/api/properties/${selectedPropertyId}/tours/floors/${floorId}`),
        {
          method: 'PATCH',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ areas })
        }
      );
      if (!res.ok) throw new Error();
      toast.success('Visibility restrictions saved');
      loadFloors();
    } catch {
      toast.error('Failed to save restrictions');
    } finally {
      setSavingFloorAreas(prev => ({ ...prev, [floorId]: false }));
    }
  };

  /* ── Render ── */
  return (
    <div className="tm-wrap">
      {/* ── Page Header ── */}
      <div className="tm-page-header">
        <div className="tm-page-icon"><FaCube /></div>
        <div>
          <h1 className="tm-page-title">3D Tour Add-on</h1>
          <p className="tm-page-sub">Manage virtual tours for any property — organise by floor and add tour links</p>
        </div>
      </div>

      {/* ── Property Selector ── */}
      <div className="tm-section">
        <label className="tm-label"><FaBuilding /> Select Property</label>
        {loadingProps ? (
          <div className="tm-loading">Loading properties…</div>
        ) : (
          <div className="tm-select-wrap">
            <select
              className="tm-select"
              value={selectedPropertyId}
              onChange={e => setSelectedPropertyId(e.target.value)}
            >
              <option value="">— Choose a property —</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.name} {p.address ? `· ${p.address}` : ''}</option>
              ))}
            </select>
            <FaChevronDown className="tm-select-caret" />
          </div>
        )}
      </div>

      {selectedPropertyId && (
        <>
          {/* ── Add Floor ── */}
          <div className="tm-section">
            <div className="tm-section-label"><FaLayerGroup /> Add Floor</div>
            <form className="tm-add-floor-form" onSubmit={handleAddFloor}>
              <div className="tm-add-floor-row">
                <input
                  className="tm-input"
                  placeholder="Floor name (e.g. Ground Floor, Floor 1)"
                  value={newFloorName}
                  onChange={e => setNewFloorName(e.target.value)}
                  required
                />
                <input
                  className="tm-input tm-input-narrow"
                  type="number"
                  placeholder="# (optional)"
                  value={newFloorNumber}
                  onChange={e => setNewFloorNumber(e.target.value)}
                  min={-5}
                  max={200}
                />
                <button className="tm-btn-primary" type="submit" disabled={addingFloor}>
                  <FaPlus /> Add Floor
                </button>
              </div>

              {/* Area restriction — inside the form so it's part of the submission */}
              <div className="tm-area-section">
                <div className="tm-area-label">
                  <FaShieldAlt /> Restrict visibility by area
                  <span className="tm-area-hint">— leave blank to show this floor to all users</span>
                </div>
                <div className="tm-area-chips">
                  {AREA_OPTIONS.map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      className={`tm-area-chip ${newFloorAreas.includes(opt.key) ? 'active' : ''}`}
                      onClick={() =>
                        setNewFloorAreas(prev =>
                          prev.includes(opt.key) ? prev.filter(k => k !== opt.key) : [...prev, opt.key]
                        )
                      }
                    >
                      {opt.label}
                      {newFloorAreas.includes(opt.key) && <FaCheck className="tm-area-chip-check" />}
                    </button>
                  ))}
                </div>
              </div>
            </form>
          </div>

          {/* ── Floors List ── */}
          <div className="tm-section">
            <div className="tm-section-label"><FaLayerGroup /> Floors &amp; Tours</div>

            {loadingFloors ? (
              <div className="tm-loading">Loading…</div>
            ) : floors.length === 0 ? (
              <div className="tm-empty">No floors added yet. Add a floor above to get started.</div>
            ) : (
              <div className="tm-floors">
                {floors.map(floor => (
                  <div key={floor.id} className={`tm-floor-card ${expandedFloor === floor.id ? 'expanded' : ''}`}>
                    {/* Floor Header */}
                    <div
                      className="tm-floor-header"
                      onClick={() => {
                        const opening = expandedFloor !== floor.id;
                        setExpandedFloor(opening ? floor.id : null);
                        if (opening && floorAreaEdits[floor.id] === undefined) {
                          setFloorAreaEdits(prev => ({ ...prev, [floor.id]: floor.areas || [] }));
                        }
                      }}
                    >
                      <div className="tm-floor-badge">
                        {floor.floor_number != null ? `F${floor.floor_number}` : '—'}
                      </div>
                      <div className="tm-floor-info">
                        <span className="tm-floor-name">{floor.floor_name}</span>
                        <div className="tm-floor-meta">
                          <span className="tm-floor-count">
                            {(floor.links || []).length} tour{(floor.links || []).length !== 1 ? 's' : ''}
                          </span>
                          {(floor.areas || []).length > 0 && (
                            <div className="tm-floor-area-badges">
                              {(floor.areas || []).map(key => {
                                const opt = AREA_OPTIONS.find(o => o.key === key);
                                return opt ? (
                                  <span key={key} className="tm-floor-area-badge">
                                    <FaShieldAlt /> {opt.label}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="tm-floor-actions" onClick={e => e.stopPropagation()}>
                        <button
                          className="tm-icon-btn tm-icon-btn-danger"
                          onClick={() => handleDeleteFloor(floor.id)}
                          title="Delete floor"
                        >
                          <FaTrash />
                        </button>
                      </div>
                      <FaChevronDown className={`tm-floor-chevron ${expandedFloor === floor.id ? 'open' : ''}`} />
                    </div>

                    {/* Floor Body */}
                    {expandedFloor === floor.id && (
                      <div className="tm-floor-body">
                        {/* Existing links */}
                        {(floor.links || []).length > 0 && (
                          <div className="tm-links-list">
                            {floor.links.map(link => (
                              <div key={link.id} className="tm-link-row">
                                <FaLink className="tm-link-icon" />
                                <a
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="tm-link-url tm-link-url-full"
                                >
                                  {link.url}
                                  <FaExternalLinkAlt />
                                </a>
                                <button
                                  className="tm-icon-btn tm-icon-btn-danger"
                                  onClick={() => handleDeleteLink(floor.id, link.id)}
                                  title="Remove link"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add new link — URL only */}
                        <form
                          className="tm-add-link-form"
                          onSubmit={e => handleAddLink(e, floor.id)}
                        >
                          <div className="tm-add-link-title">
                            <FaPlus /> Add Tour Link
                          </div>
                          <div className="tm-add-link-row">
                            <input
                              className="tm-input"
                              placeholder="Paste tour URL (e.g. https://my.matterport.com/…)"
                              value={newLinkUrl}
                              onChange={e => setNewLinkUrl(e.target.value)}
                              type="url"
                              required
                            />
                            <button className="tm-btn-primary" type="submit" disabled={addingLink}>
                              <FaCheck /> Add
                            </button>
                          </div>
                        </form>

                        {/* Restrict visibility by area — editable after creation */}
                        <div className="tm-floor-area-editor">
                          <div className="tm-add-link-title">
                            <FaShieldAlt /> Restrict Visibility by Area
                          </div>
                          <p className="tm-area-editor-hint">
                            Select the areas whose members can see this floor. Leave all unselected to show to everyone.
                          </p>
                          <div className="tm-area-chips">
                            {AREA_OPTIONS.map(opt => {
                              const selected = (floorAreaEdits[floor.id] || []).includes(opt.key);
                              return (
                                <button
                                  key={opt.key}
                                  type="button"
                                  className={`tm-area-chip ${selected ? 'active' : ''}`}
                                  onClick={() =>
                                    setFloorAreaEdits(prev => {
                                      const cur = prev[floor.id] || [];
                                      return {
                                        ...prev,
                                        [floor.id]: selected
                                          ? cur.filter(k => k !== opt.key)
                                          : [...cur, opt.key]
                                      };
                                    })
                                  }
                                >
                                  {opt.label}
                                  {selected && <FaCheck className="tm-area-chip-check" />}
                                </button>
                              );
                            })}
                          </div>
                          <button
                            className="tm-btn-primary tm-area-save-btn"
                            type="button"
                            disabled={savingFloorAreas[floor.id]}
                            onClick={() => handleSaveFloorAreas(floor.id)}
                          >
                            <FaCheck /> Save Restrictions
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
